import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { fmtSeconds, formatApiErrorDetail, CURRENCIES, fmtMoney } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Settings2, Edit3, Trash2, Clock4, Power, Camera, Search, X, Eye, EyeOff, Copy } from "lucide-react";

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const isSuper = currentUser?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // settings dialog
  const [editMeta, setEditMeta] = useState(null); // name/role dialog
  const [adjust, setAdjust] = useState(null);
  const [revealedIds, setRevealedIds] = useState({});

  // Filters
  const [businessFilter, setBusinessFilter] = useState("all"); // "all" | "unassigned" | clientId
  const [roleFilter, setRoleFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/admin/users"), api.get("/admin/clients")]);
    setUsers(u.data);
    setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const unassignedCount = users.filter((u) => u.role === "employee" && !u.client_id).length;

  const filtered = users.filter((u) => {
    // Role filter
    if (roleFilter !== "all" && u.role !== roleFilter) return false;
    // Business filter
    if (businessFilter === "unassigned") {
      if (!(u.role === "employee" && !u.client_id)) return false;
    } else if (businessFilter !== "all") {
      if (u.client_id !== businessFilter) return false;
    }
    // Search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      if (!u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const sortedUsers = [...filtered].sort((a, b) => {
    // Pull unassigned employees to the top (only when not already filtering by a specific business)
    if (businessFilter === "all") {
      const aU = a.role === "employee" && !a.client_id ? 0 : 1;
      const bU = b.role === "employee" && !b.client_id ? 0 : 1;
      if (aU !== bU) return aU - bU;
    }
    return (a.name || "").localeCompare(b.name || "");
  });

  const clearFilters = () => { setBusinessFilter("all"); setRoleFilter("all"); setSearch(""); };
  const hasActiveFilters = businessFilter !== "all" || roleFilter !== "all" || search.trim().length > 0;

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-1">[ Admin / Users ]</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Users & Employees
            </h1>
            {unassignedCount > 0 && (
              <div className="mt-2 inline-flex items-center gap-2 text-xs">
                <span className="h-2 w-2 rounded-full bg-[#d4a82a]" />
                <span className="text-stone-700">
                  <span className="font-bold">{unassignedCount}</span> new employee{unassignedCount > 1 ? "s" : ""} awaiting business assignment
                </span>
              </div>
            )}
          </div>
          <Button
            data-testid="add-user-button"
            onClick={() => setAdding(true)}
            className="rounded-md bg-[#1f4d2e] hover:bg-[#1f4d2e]/90 text-white tracking-wide"
          >
            <Plus className="h-4 w-4 mr-2" /> Add user
          </Button>
        </div>

        {/* Filter bar */}
        <div className="bg-white border border-stone-200 rounded-md p-3 mb-4 grid grid-cols-1 md:grid-cols-[1fr_220px_180px_auto] gap-2 items-center">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              data-testid="users-search-input"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-md border-stone-200 pl-9 h-10 focus-visible:ring-1 focus-visible:ring-[#1f4d2e]"
            />
          </div>
          <Select value={businessFilter} onValueChange={setBusinessFilter}>
            <SelectTrigger data-testid="business-filter-trigger" className="rounded-md h-10">
              <SelectValue placeholder="Business" />
            </SelectTrigger>
            <SelectContent className="rounded-md">
              <SelectItem value="all">All businesses</SelectItem>
              <SelectItem value="unassigned">
                <span className="inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#d4a82a]" />
                  Unassigned
                  {unassignedCount > 0 && <span className="text-stone-500">· {unassignedCount}</span>}
                </span>
              </SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="role-filter-trigger" className="rounded-md h-10">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent className="rounded-md">
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="client">Client</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters ? (
            <Button
              data-testid="clear-filters-button"
              variant="outline"
              onClick={clearFilters}
              className="rounded-md border-stone-300 h-10"
            >
              <X className="h-4 w-4 mr-1" /> Clear
            </Button>
          ) : (
            <div className="text-xs text-stone-500 text-right pr-2 hidden md:block">
              {sortedUsers.length} of {users.length}
            </div>
          )}
        </div>

        <div className="border border-stone-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-stone-50 border-b border-stone-200 text-[10px] tracking-[0.2em] uppercase text-stone-500">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Name</th>
                <th className="text-left px-4 py-3 font-bold">Email</th>
                {isSuper && <th className="text-left px-4 py-3 font-bold">Password</th>}
                <th className="text-left px-4 py-3 font-bold">Role</th>
                <th className="text-left px-4 py-3 font-bold">Client</th>
                <th className="text-right px-4 py-3 font-bold">Rate</th>
                <th className="text-center px-4 py-3 font-bold">Tracker</th>
                <th className="text-center px-4 py-3 font-bold">Screenshots</th>
                <th className="text-right px-4 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-4 py-3 font-bold">{u.name}</td>
                  <td className="px-4 py-3 font-mono-num text-xs">{u.email}</td>
                  {isSuper && (
                    <td className="px-4 py-3">
                      {u.password_plain ? (
                        <div className="flex items-center gap-2">
                          <span data-testid={`password-cell-${u.id}`} className="font-mono-num text-xs">
                            {revealedIds[u.id] ? u.password_plain : "••••••••"}
                          </span>
                          <button
                            data-testid={`toggle-password-${u.id}`}
                            onClick={() => setRevealedIds({ ...revealedIds, [u.id]: !revealedIds[u.id] })}
                            title={revealedIds[u.id] ? "Hide" : "Reveal"}
                            className="p-1 hover:bg-stone-100 rounded"
                          >
                            {revealedIds[u.id] ? <EyeOff className="h-3.5 w-3.5 text-stone-500" /> : <Eye className="h-3.5 w-3.5 text-stone-500" />}
                          </button>
                          {revealedIds[u.id] && (
                            <button
                              data-testid={`copy-password-${u.id}`}
                              onClick={() => { navigator.clipboard.writeText(u.password_plain); toast.success("Password copied"); }}
                              title="Copy"
                              className="p-1 hover:bg-stone-100 rounded"
                            >
                              <Copy className="h-3.5 w-3.5 text-stone-500" />
                            </button>
                          )}
                        </div>
                      ) : (
                        <span className="text-stone-400 text-xs italic" title="Password was set before plaintext mirroring was enabled. Reset via Edit dialog to capture it.">
                          legacy — reset to reveal
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <Badge className="rounded-md bg-stone-100 text-stone-700 hover:bg-stone-100 text-[10px] tracking-[0.15em] uppercase font-bold">
                      {u.role.replace("_", " ")}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    {u.role === "employee" && !u.client_id ? (
                      <Badge className="rounded-md bg-[#d4a82a]/15 text-[#7a5e0f] hover:bg-[#d4a82a]/15 text-[10px] tracking-[0.15em] uppercase font-bold border border-[#d4a82a]/30">
                        Unassigned
                      </Badge>
                    ) : u.role === "employee" && u.client_id ? (
                      <span className="text-stone-700">{clientName(u.client_id)}</span>
                    ) : u.role === "client" ? (
                      // Client users ARE business owners — look up their owned business (matched by user.id == client.id at signup)
                      (() => {
                        const owned = clients.find((c) => c.id === u.id);
                        return owned ? (
                          <span className="text-stone-700">
                            {owned.name}
                            <span className="ml-2 text-[9px] tracking-[0.15em] uppercase text-stone-400 font-bold">Owner</span>
                          </span>
                        ) : (
                          <span className="text-stone-400">—</span>
                        );
                      })()
                    ) : (
                      <span className="text-stone-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.role === "employee" && u.hourly_rate > 0 ? (
                      <span className="font-mono-num text-sm font-semibold text-[#1f4d2e]">
                        {fmtMoney(u.hourly_rate, u.currency)}<span className="text-stone-400 font-normal text-[10px]">/h</span>
                      </span>
                    ) : u.role === "employee" ? (
                      <span className="text-stone-400 text-xs italic">unset</span>
                    ) : (
                      <span className="text-stone-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {u.role === "employee" ? (
                      <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold ${u.tracker_enabled ? "text-[#1f4d2e]" : "text-stone-400"}`}>
                        <Power className="h-3 w-3" /> {u.tracker_enabled ? "ON" : "OFF"}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {u.role === "employee" ? (
                      <span className={`inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold ${u.screenshot_enabled ? "text-[#1f4d2e]" : "text-stone-400"}`}>
                        <Camera className="h-3 w-3" /> {u.screenshot_enabled ? `${u.screenshot_interval_sec}s` : "OFF"}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {u.role === "employee" && (
                        <>
                          <button
                            data-testid={`settings-user-${u.id}`}
                            onClick={() => setEditing(u)}
                            title="Tracker settings"
                            className="p-2 hover:bg-stone-100"
                          >
                            <Settings2 className="h-4 w-4 text-stone-700" />
                          </button>
                          <button
                            data-testid={`adjust-user-${u.id}`}
                            onClick={() => setAdjust(u)}
                            title="Adjust time"
                            className="p-2 hover:bg-stone-100"
                          >
                            <Clock4 className="h-4 w-4 text-stone-700" />
                          </button>
                        </>
                      )}
                      <button onClick={() => setEditMeta(u)} title="Edit" className="p-2 hover:bg-stone-100">
                        <Edit3 className="h-4 w-4 text-stone-700" />
                      </button>
                      <button onClick={() => remove(u.id)} title="Delete" className="p-2 hover:bg-[#a8421f]/10">
                        <Trash2 className="h-4 w-4 text-[#a8421f]" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {sortedUsers.length === 0 && (
                <tr><td colSpan={isSuper ? 9 : 8} className="text-center py-12 text-sm text-stone-500">
                  {hasActiveFilters ? "No users match the current filters." : "No users yet."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AddUserDialog open={adding} onOpenChange={setAdding} clients={clients} onSaved={load} />
      <UserSettingsDialog user={editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />
      <EditUserDialog user={editMeta} clients={clients} onOpenChange={(o) => !o && setEditMeta(null)} onSaved={load} />
      <AdjustTimeDialog user={adjust} onOpenChange={(o) => !o && setAdjust(null)} onSaved={load} />
    </AppShell>
  );
}

function AddUserDialog({ open, onOpenChange, clients, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", client_id: "none" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", { ...form, client_id: form.client_id === "none" ? null : form.client_id });
      toast.success("User created");
      onOpenChange(false);
      setForm({ name: "", email: "", password: "", role: "employee", client_id: "none" });
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-md">
        <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Add User</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Name</Label>
            <Input className="rounded-md mt-1" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Email</Label>
            <Input className="rounded-md mt-1" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Password</Label>
            <Input className="rounded-md mt-1" type="text" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-md">
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="client">Client / Business</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === "employee" && (
            <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Assign to Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-md">
                  <SelectItem value="none">Unassigned</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={saving} className="rounded-md bg-[#1f4d2e] hover:bg-[#1f4d2e]/90">
              {saving ? "Creating…" : "Create User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserSettingsDialog({ user, onOpenChange, onSaved }) {
  const { user: currentUser } = useAuth();
  const canEditBilling = currentUser?.role === "super_admin";
  const canEditCallMode = currentUser?.role === "super_admin";
  const [form, setForm] = useState({ tracker_enabled: true, screenshot_enabled: true, screenshot_interval_sec: 60, inactivity_timeout_sec: 30, auto_stop_idle_min: 0, hourly_rate: 0, currency: "PHP", call_mode_enabled: false });
  useEffect(() => {
    if (user) setForm({
      tracker_enabled: user.tracker_enabled,
      screenshot_enabled: user.screenshot_enabled,
      screenshot_interval_sec: user.screenshot_interval_sec,
      inactivity_timeout_sec: user.inactivity_timeout_sec,
      auto_stop_idle_min: user.auto_stop_idle_min ?? 0,
      hourly_rate: user.hourly_rate ?? 0,
      currency: user.currency || "PHP",
      call_mode_enabled: !!user.call_mode_enabled,
    });
  }, [user]);
  if (!user) return null;
  const save = async () => {
    try {
      await api.put(`/admin/users/${user.id}/settings`, form);
      toast.success("Settings updated");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Tracker Settings</DialogTitle>
          <div className="text-xs text-stone-500 font-mono-num">{user.email}</div>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold">Tracker enabled</div>
              <div className="text-xs text-stone-500">Master switch for time tracking</div>
            </div>
            <Switch data-testid="toggle-tracker" checked={form.tracker_enabled} onCheckedChange={(v) => setForm({ ...form, tracker_enabled: v })} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold">Screenshots enabled</div>
              <div className="text-xs text-stone-500">Periodic browser-tab screenshots</div>
            </div>
            <Switch data-testid="toggle-screenshots" checked={form.screenshot_enabled} onCheckedChange={(v) => setForm({ ...form, screenshot_enabled: v })} />
          </div>
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold">Screenshot Interval (seconds)</Label>
            <Input data-testid="input-screenshot-interval" type="number" min={5} max={3600} className="rounded-md mt-1 font-mono-num" value={form.screenshot_interval_sec} onChange={(e) => setForm({ ...form, screenshot_interval_sec: Number(e.target.value) })} />
            <div className="text-[11px] text-stone-500 mt-1">e.g. 30, 60, 300, 600</div>
          </div>
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold">Inactivity Pause (seconds)</Label>
            <Input data-testid="input-inactivity" type="number" min={5} max={3600} className="rounded-md mt-1 font-mono-num" value={form.inactivity_timeout_sec} onChange={(e) => setForm({ ...form, inactivity_timeout_sec: Number(e.target.value) })} />
            <div className="text-[11px] text-stone-500 mt-1">Pauses the paid timer when idle this long (tracker keeps running)</div>
          </div>
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold">Auto-Stop After Idle (minutes)</Label>
            <Input
              data-testid="input-auto-stop"
              type="number"
              min={0}
              max={480}
              className="rounded-md mt-1 font-mono-num"
              value={form.auto_stop_idle_min}
              onChange={(e) => setForm({ ...form, auto_stop_idle_min: Number(e.target.value) })}
            />
            <div className="text-[11px] text-stone-500 mt-1">
              <span className="font-display-italic text-[#1f4d2e]">0 = disabled.</span>{" "}
              Stops the tracker entirely after this many minutes of continuous idle.
            </div>
          </div>

          {/* Call Mode permission (super_admin only) */}
          <div className="border-t border-stone-200 pt-5 -mx-6 px-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold flex items-center gap-2">
                  Call Mode permission
                  {!canEditCallMode && (
                    <span className="text-[10px] tracking-[0.15em] uppercase text-stone-400 font-medium">Super Admin only</span>
                  )}
                </div>
                <div className="text-xs text-stone-500">Allow this employee to use the "Call Mode" button (productive without input — for cold-callers).</div>
                <div className="text-[10px] text-stone-400 mt-0.5 font-display-italic">Business owners can also toggle this from their dashboard.</div>
              </div>
              <Switch
                data-testid="toggle-call-mode-permission"
                checked={form.call_mode_enabled}
                onCheckedChange={(v) => setForm({ ...form, call_mode_enabled: v })}
                disabled={!canEditCallMode}
              />
            </div>
          </div>

          <div className="border-t border-stone-200 pt-5 -mx-6 px-6">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#1f4d2e] mb-3 flex items-center gap-2">
              <span className="h-px w-6 bg-[#d4a82a]" />
              Billing
              {!canEditBilling && (
                <span className="ml-auto text-[10px] tracking-[0.15em] uppercase text-stone-400 font-medium">
                  Super Admin only
                </span>
              )}
            </div>
            <div className="grid grid-cols-[1fr_140px] gap-3">
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold">Hourly Rate</Label>
                <Input
                  data-testid="input-hourly-rate"
                  type="number"
                  min={0}
                  step="0.01"
                  className="rounded-md mt-1 font-mono-num"
                  value={form.hourly_rate}
                  onChange={(e) => setForm({ ...form, hourly_rate: Number(e.target.value) })}
                  placeholder="0.00"
                  disabled={!canEditBilling}
                />
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })} disabled={!canEditBilling}>
                  <SelectTrigger data-testid="select-currency" className="rounded-md mt-1 font-mono-num">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-md">
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className="font-mono-num mr-2">{c.symbol}</span>{c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-[11px] text-stone-500 mt-2">
              {canEditBilling
                ? "Used to compute billable earnings on reports & dashboards. Productive hours × rate."
                : <span className="font-display-italic">Only the Super Admin can change rates and currency.</span>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="save-settings-button" onClick={save} className="rounded-md bg-[#1f4d2e] hover:bg-[#1f4d2e]/90">Save Settings</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, clients, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", role: "employee", client_id: "none", password: "" });
  useEffect(() => {
    if (user) setForm({ name: user.name, role: user.role, client_id: user.client_id || "none", password: "" });
  }, [user]);
  if (!user) return null;
  const save = async () => {
    const payload = { name: form.name, role: form.role, client_id: form.client_id === "none" ? null : form.client_id };
    if (form.password) payload.password = form.password;
    try {
      await api.put(`/admin/users/${user.id}`, payload);
      toast.success("User updated");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-md">
        <DialogHeader><DialogTitle className="font-display text-xl tracking-tight">Edit User</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Name</Label>
            <Input className="rounded-md mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-md">
                <SelectItem value="employee">Employee</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === "employee" && (
            <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-md">
                  <SelectItem value="none">Unassigned</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Reset Password (optional)</Label>
            <Input className="rounded-md mt-1" type="text" placeholder="Leave blank to keep" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} className="rounded-md bg-[#1f4d2e] hover:bg-[#1f4d2e]/90">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustTimeDialog({ user, onOpenChange, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [minutes, setMinutes] = useState(0);
  const [op, setOp] = useState("add");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(today);
  useEffect(() => { if (user) setDate(today); }, [user, today]);
  if (!user) return null;
  const save = async () => {
    const delta = (op === "add" ? 1 : -1) * Math.round(minutes * 60);
    if (!reason.trim()) { toast.error("Reason required"); return; }
    if (!date) { toast.error("Pick a date"); return; }
    try {
      await api.post(`/admin/users/${user.id}/adjust-time`, { delta_seconds: delta, reason, date });
      toast.success(`${op === "add" ? "Added" : "Deducted"} ${minutes}m on ${date}`);
      onOpenChange(false);
      setMinutes(0); setReason(""); setOp("add"); setDate(today);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Adjust Time</DialogTitle>
          <div className="text-xs text-stone-500 font-mono-num">{user.email}</div>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold">Date</Label>
            <Input
              data-testid="adjust-date"
              type="date"
              max={today}
              className="rounded-md mt-1 font-mono-num"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            <div className="text-[11px] text-stone-500 mt-1 font-display-italic">
              Adjustment will be attached to this date — past days are OK.
            </div>
          </div>
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Action</Label>
            <Select value={op} onValueChange={setOp}>
              <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-md">
                <SelectItem value="add">Add time</SelectItem>
                <SelectItem value="deduct">Deduct time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Minutes</Label>
            <Input data-testid="adjust-minutes" type="number" min={0} step={1} className="rounded-md mt-1 font-mono-num" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} />
          </div>
          <div><Label className="text-xs tracking-[0.2em] uppercase font-bold">Reason</Label>
            <Input className="rounded-md mt-1" placeholder="e.g. Forgot to start tracker" value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="adjust-submit-button" onClick={save} className="rounded-md bg-[#1f4d2e] hover:bg-[#1f4d2e]/90">Apply Adjustment</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
